import {
    FailureDetails,
    resolveFailureDetails,
} from "./resolveFailureReason";
import {
    getWalletErrorDiagnostics,
    isUnattemptedIntent,
    type WalletErrorDiagnostics,
} from "./walletErrorDiagnostics";
import { asOpaqueToken, asRecord, readProperty } from "./safeErrorRead";
import { allowlistedErrorName } from "~/utils/errorTelemetry";
import { CARD_DETAILS_ERROR_MESSAGE } from "~/constants/checkout";

/**
 * Reading and classifying the errors the checkout gets back from ChargeBee,
 * Stripe and our own API.
 *
 * Pure, and knows nothing about logging; `checkoutInstrumentation.ts` decides
 * what to do with the answers.
 *
 * Two rules hold throughout. Errors arrive from SDKs and sometimes from other
 * realms, so every read of an untrusted error is defensive — an accessor may
 * throw rather than answer. And nothing unrecognised is copied into telemetry:
 * provider codes are allow-listed by value and the vendor request id by shape,
 * so free-form vendor prose can never reach a log field.
 */

export const CARD_TOKENIZE_FAILURE_CATEGORY = {
    AUTHENTICATION: "authentication",
    CARD_VALIDATION: "card_validation",
    INVALID_REQUEST: "invalid_request",
    INVALID_REQUEST_REJECTED: "invalid_request_rejected",
    ISSUER_DECLINE: "issuer_decline",
    NETWORK: "network",
    /**
     * ChargeBee rejected with an intent the gateway never saw: `inited`, no
     * payment attempt, no code. Nothing reached the bank, so this is a sheet
     * that closed or an SDK that gave up, not a decline.
     */
    NOT_ATTEMPTED: "not_attempted",
    TIMEOUT: "timeout",
    UNKNOWN: "unknown",
} as const;

export type CardTokenizeFailureCategory =
    (typeof CARD_TOKENIZE_FAILURE_CATEGORY)[keyof typeof CARD_TOKENIZE_FAILURE_CATEGORY];

/**
 * How long an authorize has to hang before its `invalid_request` can be
 * ChargeBee's internal ~40 s timeout. Anything quicker is a request the
 * gateway rejected outright, which a retry would only repeat.
 */
export const CHARGEBEE_AUTHORIZE_TIMEOUT_FLOOR_MS = 30_000;

/**
 * `invalid_request` is ChargeBee's generic error type, not a timeout. Only the
 * authorize's duration tells the internal timeout apart from an outright
 * rejection, so an authorize-stage throw with no duration counts as rejected.
 * Errors from before the gateway (create-intent) must not come through here.
 * The timeout keeps `invalid_request`, so its Error Tracking issue and
 * dashboards carry on, and a rejection opens an issue of its own instead of
 * hiding among them.
 */
export function splitInvalidRequestByAuthorizeTime(
    failureCategory: CardTokenizeFailureCategory,
    authorizeDurationMs: number | undefined
): CardTokenizeFailureCategory {
    if (failureCategory !== CARD_TOKENIZE_FAILURE_CATEGORY.INVALID_REQUEST) {
        return failureCategory;
    }

    const hitGatewayTimeout =
        authorizeDurationMs !== undefined &&
        authorizeDurationMs >= CHARGEBEE_AUTHORIZE_TIMEOUT_FLOOR_MS;
    return hitGatewayTimeout
        ? CARD_TOKENIZE_FAILURE_CATEGORY.INVALID_REQUEST
        : CARD_TOKENIZE_FAILURE_CATEGORY.INVALID_REQUEST_REJECTED;
}

export type CardTokenizeErrorContext = {
    failureCategory: CardTokenizeFailureCategory;
    /** The thrower's name, allow-listed. Never the raw value. */
    errorName: string;
    providerErrorCode?: string;
    providerHttpStatus?: number;
    /** The vendor's id for the failed request, when it gave us one. */
    vendorRequestId?: string;
} & WalletErrorDiagnostics;

const SHARED_PROVIDER_ERROR_VALUE = {
    CARD_DECLINED: "card_declined",
    INVALID_REQUEST: "invalid_request",
    PAYMENT_INTENT_AUTHENTICATION_FAILURE:
        "payment_intent_authentication_failure",
} as const;

const ALLOWED_ERROR_NAMES = new Map(
    [
        "Error",
        "TypeError",
        "AbortError",
        "AxiosError",
        "PAYMENT_ATTEMPT_REFUSED",
        SHARED_PROVIDER_ERROR_VALUE.INVALID_REQUEST,
        "INVALID_CARD",
        "CARD_CVV_INVALID",
        "CARD_EXPIRY_INVALID",
        "CARD_EXPIRY_PAST",
        "CARD_NUMBER_INVALID",
        "CARD_NUMBER_INCOMPLETE",
        SHARED_PROVIDER_ERROR_VALUE.PAYMENT_INTENT_AUTHENTICATION_FAILURE,
        SHARED_PROVIDER_ERROR_VALUE.CARD_DECLINED,
        "REPLY_TIMEOUT",
    ].map((value) => [value.toLowerCase(), value])
);

// Telemetry vocabulary, not an exhaustive provider schema. These literals are
// observed in our checkout payloads/logs or existing ChargeBee error handling;
// an unknown value is omitted instead of being copied into telemetry.
const ALLOWED_PROVIDER_ERROR_CODES = new Map(
    [
        SHARED_PROVIDER_ERROR_VALUE.CARD_DECLINED,
        "card_expiry_incomplete",
        "card_expiry_invalid",
        "card_expiry_past",
        "card_cvv_incomplete",
        "card_cvv_invalid",
        "card_number_incomplete",
        "card_number_invalid",
        "incorrect_number",
        "processing_error",
        "incorrect_cvc",
        "invalid_cvc",
        "expired_card",
        "invalid_card",
        SHARED_PROVIDER_ERROR_VALUE.INVALID_REQUEST,
        SHARED_PROVIDER_ERROR_VALUE.PAYMENT_INTENT_AUTHENTICATION_FAILURE,
        // Issuer decline reasons observed on the wallet path, which reaches the
        // same gateway as the card form. Listed so the reason survives into
        // telemetry as itself rather than as a blanket `card_declined`.
        "insufficient_funds",
        "do_not_honor",
        "generic_decline",
        "call_issuer",
        "try_again_later",
        "card_velocity_exceeded",
        "transaction_not_allowed",
        "restricted_card",
        "stolen_card",
        "lost_card",
        "invalid_account",
        "revocation_of_authorization",
        "authentication_required",
        "payment_method_not_available",
    ].map((value) => [value.toLowerCase(), value])
);

/** `instanceof` throws on a revoked proxy, and these values are not ours. */
function isErrorInstance(value: unknown): value is Error {
    try {
        return value instanceof Error;
    } catch {
        return false;
    }
}

function getErrorSources(error: unknown): Array<Record<string, unknown>> {
    const root = asRecord(error);
    if (!root) return [];

    return [
        root,
        asRecord(readProperty(root, "error")),
        asRecord(readProperty(root, "cause")),
    ].filter((source): source is Record<string, unknown> => source !== null);
}

function readAllowlistedString(
    sources: Array<Record<string, unknown>>,
    keys: string[],
    allowedValues: ReadonlyMap<string, string>
): string | undefined {
    for (const source of sources) {
        for (const key of keys) {
            try {
                const value = source[key];
                if (typeof value === "string" && value.trim()) {
                    // A value outside the allow-list is not an answer: keep
                    // looking, a further key or source may carry a known one.
                    const allowed = allowedValues.get(
                        value.trim().toLowerCase()
                    );
                    if (allowed) return allowed;
                }
            } catch {
                // Ignore hostile or throwing property accessors from SDK errors.
            }
        }
    }

    return undefined;
}

/**
 * The categories readable from a signature alone. Timeout and network are
 * deliberately absent: they are transport-shaped and must not outrank the
 * gateway's own verdict (see `classifyCardTokenizeFailure`).
 */
function classifyBySignature(
    signature: string
): CardTokenizeFailureCategory | undefined {
    if (/authentication|3ds|3d secure/.test(signature)) {
        return CARD_TOKENIZE_FAILURE_CATEGORY.AUTHENTICATION;
    }
    if (signature.includes(SHARED_PROVIDER_ERROR_VALUE.INVALID_REQUEST)) {
        return CARD_TOKENIZE_FAILURE_CATEGORY.INVALID_REQUEST;
    }
    // `card_expiry_past` and friends are ChargeBee's own field validation — a
    // date the customer typed. `expired_card` is the gateway's verdict on a card
    // it was handed, which is why it sits in the decline branch below instead:
    // on the wallet path nobody typed anything, and filing "Your card has
    // expired." as our defect is how a decline dashboard fills up with noise.
    if (
        /invalid[ _](card|cvv|cvc|expiry|number)|card_(number|cvv|expiry)|incorrect_(number|cvc)|invalid_cvc|incomplete/.test(
            signature
        )
    ) {
        return CARD_TOKENIZE_FAILURE_CATEGORY.CARD_VALIDATION;
    }
    // Matched on the code as well as the prose, so classification does not
    // depend on the gateway's wording staying in English.
    if (
        /payment_attempt_refused|payment(?: method)?[ _](?:was[ _])?declined|card[ _](?:was[ _])?declined|card[ _](?:has[ _])?expired|insufficient[ _]funds|do_not_honor|generic_decline|call_issuer|try_again_later|card_velocity_exceeded|transaction_not_allowed|restricted_card|stolen_card|lost_card|invalid_account|revocation_of_authorization|expired_card|does not support this type of purchase/.test(
            signature
        )
    ) {
        return CARD_TOKENIZE_FAILURE_CATEGORY.ISSUER_DECLINE;
    }

    return undefined;
}

function classifyCardTokenizeFailure(
    errorName: string | undefined,
    providerErrorCode: string | undefined,
    errorMessage: string
): CardTokenizeFailureCategory {
    // The allow-listed provider code is the gateway's own verdict on the card,
    // so it is read first: an SDK decline delivered as an `AxiosError` would
    // otherwise be filed as a network failure.
    const fromProviderCode = providerErrorCode
        ? classifyBySignature(providerErrorCode.toLowerCase())
        : undefined;
    if (fromProviderCode) return fromProviderCode;

    const signature = [errorName, providerErrorCode, errorMessage]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

    if (/timeout|reply_timeout|timed out/.test(signature)) {
        return CARD_TOKENIZE_FAILURE_CATEGORY.TIMEOUT;
    }
    if (/axios|network|failed to fetch|connection/.test(signature)) {
        return CARD_TOKENIZE_FAILURE_CATEGORY.NETWORK;
    }

    return (
        classifyBySignature(signature) ?? CARD_TOKENIZE_FAILURE_CATEGORY.UNKNOWN
    );
}

/**
 * `resolveFailureDetails` reads untrusted error objects with plain property
 * access, so a hostile or throwing accessor on an SDK error takes down whoever
 * asked. Exported so a failure path that needs the details for its own log
 * resolves them the same safe way this module does.
 */
export function getFailureDetails(error: unknown): FailureDetails {
    try {
        return resolveFailureDetails(error);
    } catch {
        return {};
    }
}

/**
 * A gateway error code as telemetry may carry it: an allow-listed value or
 * nothing. Shared by `code` and `decline_code`, which speak the same
 * vocabulary, so both are constrained by the same list.
 */
export function getAllowlistedProviderErrorCode(
    code: string | undefined
): string | undefined {
    return code
        ? ALLOWED_PROVIDER_ERROR_CODES.get(code.toLowerCase())
        : undefined;
}

/** Stripe answers with `request_id` as a field; ChargeBee does not. */
const REQUEST_ID_KEYS = ["request_id", "requestId", "request-id"] as const;

/**
 * ChargeBee only appends its request id to the `error_text` summary
 * ("…; code: invalid_request; request-id: abc123"), so it has to be parsed out
 * of prose.
 */
// Case folding is the `i` flag's job, so spelling both cases in the class only
// says the same thing twice.
const REQUEST_ID_IN_TEXT = /request[-_ ]?id:\s*([a-z0-9_-]+)/i;
const TEXT_KEYS_CARRYING_REQUEST_ID = ["error_text", "message"] as const;

/**
 * An opaque vendor token cannot be allow-listed by value, so it is constrained
 * by shape: the keys it is read from also carry free-form vendor prose, which
 * must not land in a telemetry field.
 */
const MAX_REQUEST_ID_LENGTH = 64;

function asRequestId(value: unknown): string | undefined {
    return asOpaqueToken(value, MAX_REQUEST_ID_LENGTH);
}

/**
 * The vendor's own id for the failed request, so a failure we cannot explain
 * from our side can be escalated to ChargeBee or Stripe as a specific request.
 * The missing piece for the 41-second `invalid_request` hang.
 */
function readVendorRequestId(
    sources: Array<Record<string, unknown>>
): string | undefined {
    // A dedicated field outranks prose anywhere, so every source is asked for
    // the keys before any text is parsed.
    for (const source of sources) {
        for (const key of REQUEST_ID_KEYS) {
            const requestId = asRequestId(readProperty(source, key));
            if (requestId) return requestId;
        }
    }

    for (const source of sources) {
        for (const key of TEXT_KEYS_CARRYING_REQUEST_ID) {
            const text = readProperty(source, key);
            if (typeof text !== "string") continue;

            const requestId = asRequestId(REQUEST_ID_IN_TEXT.exec(text)?.[1]);
            if (requestId) return requestId;
        }
    }

    return undefined;
}

function getCardTokenizeClassificationMessage(
    error: unknown,
    resolvedMessage = getFailureDetails(error).message
): string {
    // An `Error`'s own message outranks the resolved one, but reading
    // `message` off any object would let a generic top-level `message` beat
    // the gateway's `error_message` that the walk found.
    const ownMessage = isErrorInstance(error) && readProperty(error, "message");
    // `new Error()` carries `""`, and an SDK wrapping a gateway rejection that
    // way would classify as `unknown` with its `error_message` in hand.
    if (typeof ownMessage === "string" && ownMessage.trim()) return ownMessage;
    if (resolvedMessage) return resolvedMessage;

    try {
        return String(error);
    } catch {
        // A value whose `toString` throws leaves nothing to classify on. The
        // customer-facing wording stands in because it is what the same
        // failure puts on screen, so the log and the screen agree — and
        // because it is the constant, this copy cannot drift from that one.
        return CARD_DETAILS_ERROR_MESSAGE;
    }
}

/**
 * Adds a controlled scalar subset to the existing error telemetry. Provider
 * codes are allow-listed and HTTP status is numeric-only; the raw SDK object is
 * never spread into the log payload.
 *
 * Pass `details` when the caller has already resolved them, so one failure is
 * walked once no matter how many questions are asked about it.
 */
export function getCardTokenizeErrorContext(
    error: unknown,
    details: FailureDetails = getFailureDetails(error)
): CardTokenizeErrorContext {
    const sources = getErrorSources(error);
    const classificationMessage = getCardTokenizeClassificationMessage(
        error,
        details.message
    );
    // `name` is read defensively as well: an `Error` whose `name` was
    // reassigned to a non-string turns `.toLowerCase()` into a TypeError.
    const ownName = isErrorInstance(error) && readProperty(error, "name");
    const errorName =
        readAllowlistedString(sources, ["name"], ALLOWED_ERROR_NAMES) ??
        (typeof ownName === "string"
            ? ALLOWED_ERROR_NAMES.get(ownName.toLowerCase())
            : undefined);
    const providerErrorCode = getAllowlistedProviderErrorCode(details.code);
    const walletDiagnostics = getWalletErrorDiagnostics(error);
    const classifiedCategory = classifyCardTokenizeFailure(
        errorName,
        providerErrorCode,
        classificationMessage
    );
    // Only in place of `unknown`: any code or message the gateway gave us is
    // its own verdict and outranks the shape of the object it came in.
    const failureCategory =
        classifiedCategory === CARD_TOKENIZE_FAILURE_CATEGORY.UNKNOWN &&
        isUnattemptedIntent(walletDiagnostics)
            ? CARD_TOKENIZE_FAILURE_CATEGORY.NOT_ATTEMPTED
            : classifiedCategory;

    return {
        failureCategory,
        // Payment vocabulary first, since it carries the gateway's own names,
        // then the platform list; the shared fallback keeps an unrecognised
        // name as a count rather than nothing.
        errorName: errorName ?? allowlistedErrorName(error),
        providerErrorCode,
        providerHttpStatus: details.providerHttpStatus,
        vendorRequestId: readVendorRequestId(sources),
        // Key names / allow-listed statuses / shaped id only. The raw intent
        // still stays out of the log — these are what is left when there is
        // no `error_detail` and `failureDetail` collapses to "Unknown error".
        ...walletDiagnostics,
    };
}
